import * as React from "react";
import { observer, inject } from "mobx-react";

import { AppStore, IngestionStore } from "Stores/index";
import {Button, Col, Form, FormControl, FormGroup, Row} from "react-bootstrap";
import { SelectWrapper } from "../../Common/SelectWrapper";

interface CourseMappingFormState {
    overridden: boolean,
    overrideNeedsReview: any,
    overrideDisabled: any,
    overrideName: string,
    overrideCourseId: string,
    overrideTermId: number | undefined,
    overrideComment: string,
    overriddenBy: string | undefined,
}

interface CourseMappingFormProps {
    mapping: any
    handleCancel: () => void
    handleSave: () => void
    appStore?: AppStore
    ingestionStore?: IngestionStore
}


@inject("appStore", "ingestionStore")
@observer
export class CourseMappingForm extends React.Component<CourseMappingFormProps, CourseMappingFormState> {

    state = {
        overridden: false,
        overrideNeedsReview: undefined,
        overrideDisabled: undefined,
        overrideName: "",
        overrideCourseId: "",
        overrideTermId: undefined,
        overrideComment: "",
        overriddenBy: undefined
    };

    saveUserOverride = () => {
        if (!this.props.ingestionStore || !this.props.ingestionStore.courseMapping) return;

        const {overrideNeedsReview, overrideDisabled, overrideName, overrideCourseId, overrideTermId, overrideComment} = this.state;
        const toSend = {
            enflux_course_id: this.props.ingestionStore.courseMapping.enflux_course_id,
            term_id: overrideTermId ? overrideTermId : null,
            name: overrideName === "" ? null : overrideName,
            needs_review: overrideNeedsReview,
            disabled: overrideDisabled,
            course_id: overrideCourseId === "" ? null : overrideCourseId,
            override_comment: overrideComment
        };

        (this.props.ingestionStore.courseMapping.user_override
            ? this.props.ingestionStore.updateUserOverride(this.props.ingestionStore.courseMapping.user_override.id, toSend)
            : this.props.ingestionStore.createUserOverride(toSend))
            .then(() => {
                const errors = this.props.appStore!.errors ? this.props.appStore!.errors.length > 0 : false;
                if (!errors) {
                    this.props.handleSave();
                    this.props.ingestionStore!.fetchEnfluxCourses();
                }
            });
    };

    componentDidMount() {
        if (!this.props.ingestionStore) return;
        this.props.ingestionStore.fetchTerms();
        this.props.ingestionStore.fetchCourseMapping(this.props.mapping.id).then(() => {
            const user_override = this.props.ingestionStore!.courseMapping.user_override;
            if (user_override) {
                this.setState({
                    overridden: true,
                    overrideNeedsReview: user_override.needs_review,
                    overrideDisabled: user_override.disabled,
                    overrideName: user_override.name,
                    overrideCourseId: user_override.course_id,
                    overrideTermId: user_override.term_id,
                    overriddenBy: user_override.overridden_by || "Unknown",
                    overrideComment: user_override.override_comment
                });
            } else {
                this.setState( { overridden: false });
            }
        });
    }

    updateNeedsReview = (item: any) => {
        this.setState({ overrideNeedsReview: item ? item.value : null });
    };

    updateDisabled = (item: any) => {
        this.setState({ overrideDisabled: item ? item.value : null });
    };

    handleDisabledToggle = (checked: boolean) => {
        this.setState({ overrideDisabled: checked });
    };

    updateInput = (event: any) => {
        const {target: {name, value}} = event;
        this.setState({ [name]: value } as Pick<CourseMappingFormState, keyof CourseMappingFormState>);
    };

    updateTerm = (term: any) => {
        const termId = term ? term.id : null;
        this.setState( { overrideTermId: termId });
    };

    findTerm = (term_id: number) => {
        if (!term_id) return "";
        const term = this.props.ingestionStore!.terms.find(({ id }) => id === term_id);
        return term ? term.name : "";
    };

    getTermValue = obj => obj.id;
    getTermLabel = obj => obj.term;

    getToggleValue = obj => obj.value;
    getToggleLabel = obj => obj.label;

    renderDatasources () {
        if (!this.props.ingestionStore || !this.props.ingestionStore.courseMapping || !this.props.ingestionStore.courseMapping.datasources || !this.props.ingestionStore.terms) return;
        return (
            <React.Fragment>
                {this.props.ingestionStore.courseMapping.datasources.map((datasource, index) => (
                    <div key={index} className="separator">
                        <Row>
                            <Col className="row-padding-top font-italic">Source: {datasource.source_table}</Col>
                        </Row>
                        <Row className="row-padding">
                            <Col md={2} className={datasource.needs_review ? "icon icon-check" : ""}/>
                            <Col md={2} className={datasource.disabled ? "icon icon-check" : ""}/>
                            <Col md={4}>{datasource.name}</Col>
                            <Col md={2}>{datasource.course_id}</Col>
                            <Col md={2}>{this.findTerm(datasource.term_id)}</Col>
                        </Row>
                        { this.renderDatasourceOverriddenBy(datasource.overridden_by) }
                    </div>
                ))}
            </React.Fragment>
        );
    }

    renderDatasourceOverriddenBy(overriddenBy: string) {
        if (overriddenBy) {
            return (
                <Row>
                    <Col className="row-padding-bottom text-muted text-right">
                        Overridden By: {overriddenBy}
                    </Col>
                </Row>
            );
        }
        return null;
    }

    renderUserOverrides () {
        if (!this.props.ingestionStore || !this.props.ingestionStore.courseMapping || !this.props.ingestionStore.terms) return;
        const options = [
            {
                value: true,
                label: "Yes",
            }, {
                value: false,
                label: "No",
            },
        ];
        return (
            <React.Fragment>
                <Row>
                    <Col className="row-padding-top font-italic">Source: enflux_user_course_overrides</Col>
                </Row>
                <Row className="row-padding">
                    <Col md={2} className="font-weight-bold">Needs Review</Col>
                    <Col md={2} className="font-weight-bold">Disabled</Col>
                    <Col md={4} className="font-weight-bold">Name</Col>
                    <Col md={2} className="font-weight-bold">Course ID</Col>
                    <Col md={2} className="font-weight-bold">Term</Col>
                </Row>
                <Row className="row-padding">
                    <Col md={2}>
                        <SelectWrapper
                            name="overrideNeedsReviewSelect"
                            isClearable={true}
                            getOptionValue={this.getToggleValue}
                            getOptionLabel={this.getToggleLabel}
                            options={options}
                            value={ options.find(obj => obj.value === this.state.overrideNeedsReview) }
                            onChange={this.updateNeedsReview}/>
                    </Col>
                    <Col md={2}>
                        <SelectWrapper
                            name="overrideDisabledSelect"
                            isClearable={true}
                            getOptionValue={this.getToggleValue}
                            getOptionLabel={this.getToggleLabel}
                            options={options}
                            value={ options.find(obj => obj.value === this.state.overrideDisabled) }
                            onChange={this.updateDisabled}/>
                    </Col>
                    <Col md={4}>
                        <FormControl
                            name="overrideName"
                            placeholder="No override defined"
                            type="text"
                            required={false}
                            value={this.state.overrideName || ""}
                            onChange={this.updateInput}
                        />
                    </Col>
                    <Col md={2}>
                        <FormControl
                            name="overrideCourseId"
                            placeholder="No override defined"
                            type="text"
                            required={false}
                            value={this.state.overrideCourseId || ""}
                            onChange={this.updateInput}
                        />
                    </Col>
                    <Col md={2}>
                        <SelectWrapper
                            name="overrideTermSelect"
                            isClearable={true}
                            getOptionValue={this.getTermValue}
                            getOptionLabel={this.getTermLabel}
                            options={this.props.ingestionStore.terms.slice()}
                            value={this.props.ingestionStore.terms.find(obj => obj.id === this.state.overrideTermId)}
                            onChange={this.updateTerm}/>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormGroup controlId="dashboard-name">
                            <Form.Label>Comments:</Form.Label>
                            <FormControl
                                name="overrideComment"
                                as="textarea"
                                required={false}
                                value={this.state.overrideComment || ""}
                                onChange={this.updateInput}/>
                        </FormGroup>
                    </Col>
                </Row>
                { this.renderOverriddenBy() }
            </React.Fragment>
        );
    }

    renderOverriddenBy() {
        if (this.state.overridden) {
            return (
                <Row>
                    <Col className="text-muted text-right">
                        Last Overridden By: {this.state.overriddenBy}
                    </Col>
                </Row>
            );
        }
        return null;
    }

    render() {
        const {ingestionStore} = this.props;

        if (!ingestionStore) {
            console.error("ingestion store not loaded");
            return null;
        } else {
            const courseMapping = this.props.ingestionStore!.courseMapping;
            const existingOverride = courseMapping && courseMapping.user_override;
            return (
                <div className="table-expander">
                    <div className="separator">
                    { this.renderDatasources() }
                    </div>
                    { this.renderUserOverrides() }
                    <Row className="margin-top">
                        <Col className="text-end">
                            <Button
                                variant="secondary"
                                size="lg"
                                id="user-form-cancel"
                                onClick={() => {
                                    this.props.handleCancel();
                                }}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                size="lg"
                                id="user-form-save"
                                style={{marginLeft: 15}}
                                onClick={this.saveUserOverride}>
                                { existingOverride ? "Update" : "Create" }
                            </Button>
                        </Col>
                    </Row>
                </div>
            );
        }
    }
}
